import { App, Form, Input, Select, Typography } from 'antd';
import { UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { http } from '../../api/client.js';
import { FancyModal } from '../../components/ui/index.js';
import { tx } from '../../i18n/translate.js';

interface BuOption {
  businessunitid: string;
  name: string;
}

interface CreateUserFormValues {
  fullname: string;
  domainname: string;
  internalemailaddress?: string;
  businessunitid?: string;
  password?: string;
}

/**
 * 新建用户 Modal (M39)
 *
 * 行业标准: 企业系统创建用户的最小集合
 *   - 必填: 姓名、登录账号 (domain 或 email)
 *   - 推荐: 部门 / 初始密码 (用户首次登录改) / 初始角色
 *   - 邮箱可选 (海外销售用 email 登录、国内用 domain)
 */
export function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateUserFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [bus, setBus] = useState<BuOption[]>([]);

  // 用户、组织和任职在 CREST 维护；角色来源统一在权限中心配置。
  useEffect(() => {
    if (!open) return;
    http
      .get<{ nodes: Array<{ key: string; title: string; children: unknown[] }> }>(
        '/admin/users/bu-tree',
      )
      .then((buRes) => {
        // 把树形 BU 拍平
        const flat: BuOption[] = [];
        const walk = (nodes: Array<{ key: string; title: string; children: unknown[] }>) => {
          for (const n of nodes) {
            flat.push({ businessunitid: n.key, name: n.title });
            walk(n.children as Array<{ key: string; title: string; children: unknown[] }>);
          }
        };
        walk(buRes.data.nodes);
        setBus(flat);
      })
      .catch(() => message.warning(tx('hardcoded:pages.admin.createusermodal.74fe3875')));
  }, [open, message]);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await http.post('/admin/users', {
        fullname: values.fullname,
        domainname: values.domainname,
        internalemailaddress: values.internalemailaddress?.trim() || null,
        businessunitid: values.businessunitid ?? null,
        password: values.password?.trim() || undefined,
      });
      message.success(
        tx('hardcoded:pages.admin.createusermodal.cc45e1bd', { v0: values.fullname }),
      );
      form.resetFields();
      onCreated();
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        tx('hardcoded:pages.admin.createusermodal.0977723f', {
          v0: err.response?.data?.message ?? tx('hardcoded:pages.admin.createusermodal.21febb7a'),
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FancyModal
      open={open}
      onCancel={onClose}
      onOk={submit}
      title={tx('hardcoded:pages.admin.createusermodal.9155ed1f')}
      subtitle={
        <span>
          {tx('hardcoded:pages.admin.createusermodal.2385b10d')}
          <span style={{ color: '#ef4444', fontWeight: 600 }}>*</span>{' '}
          {tx('hardcoded:pages.admin.createusermodal.e48adac7')}
        </span>
      }
      icon={<UserPlus size={18} />}
      okText={tx('hardcoded:pages.admin.createusermodal.02203f53')}
      cancelText={tx('hardcoded:pages.admin.createusermodal.312aa610')}
      confirmLoading={submitting}
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label={tx('legacy:pages.admin.createusermodal.90984ef9')}
          name="fullname"
          rules={[
            { required: true, message: tx('hardcoded:pages.admin.createusermodal.34778a61') },
          ]}
        >
          <Input placeholder={tx('hardcoded:pages.admin.createusermodal.0d9a67f3')} allowClear />
        </Form.Item>

        <Form.Item
          label={tx('legacy:pages.admin.createusermodal.b8a81f06')}
          name="domainname"
          rules={[
            { required: true, message: tx('hardcoded:pages.admin.createusermodal.e0789d1e') },
          ]}
          tooltip={tx('hardcoded:pages.admin.createusermodal.e4f8ac79')}
        >
          <Input placeholder={tx('hardcoded:pages.admin.createusermodal.8b8211a3')} allowClear />
        </Form.Item>

        <Form.Item
          label={tx('legacy:pages.admin.createusermodal.95ad87df')}
          name="internalemailaddress"
        >
          <Input
            type="email"
            placeholder={tx('hardcoded:pages.admin.createusermodal.1029a3bf')}
            allowClear
          />
        </Form.Item>

        <Form.Item label={tx('legacy:pages.admin.createusermodal.e3e67201')} name="businessunitid">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={tx('hardcoded:pages.admin.createusermodal.b5d272ae')}
            options={bus.map((b) => ({ value: b.businessunitid, label: b.name }))}
          />
        </Form.Item>

        <Form.Item
          label={tx('legacy:pages.admin.createusermodal.e2395cb8')}
          name="password"
          tooltip={tx('hardcoded:pages.admin.createusermodal.55bb6f98')}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {tx('hardcoded:pages.admin.createusermodal.003cac72')}
            </Typography.Text>
          }
        >
          <Input.Password
            placeholder={tx('hardcoded:pages.admin.createusermodal.923cbde8')}
            allowClear
          />
        </Form.Item>
      </Form>
    </FancyModal>
  );
}
